// Kyte pricingBreakdown parsing — pure helpers operating on the
// `pricingBreakdown[]` rows Kyte attaches to a booking. The orchestration
// file at ../ancillaries.js used to own this whole subsystem; pulling it
// out keeps that file focused on book/fetch/diff orchestration.
//
// The breakdown rows aggregate by (passengerId, type, code) and use
// synthetic ids for seats. Two derived shapes drive everything here:
//   - byId      `${pax}:${type}:${bare_id}` → minor units
//   - byPaxType `${pax}:${type}` → { sum, codes[] }
// build_ancillary_price_index produces both; the rest of the module is
// matching logic against them.
//
// Two public entry points are exported. Everything else stays internal
// so the boundary stays small and the call shapes match what
// ../ancillaries.js used to inline.

import { currency_symbol } from '@resources';
import {
  as_number,
  as_array,
  make_price,
  minor_to_decimal,
} from '../base/helpers';
import {
  SHOPPED_ANCILLARY_TYPES,
  is_ancillary_row,
  is_bundle_service,
  strip_pax_suffix,
  base_code,
  strip_meta,
} from './resources/ancillaries';
import { realign_segment_ids } from '@liveflight/utilities/mixins';

/**
 * Sum shopped pricingBreakdown rows in minor units (bag / seat /
 * sportsEquipment + non-bundle service rows). Used by
 * build_retrieve_price to derive `total = totalCost − shopped`.
 *
 * @param {object} booking
 * @returns {number} minor units
 */
const sum_shopped_ancillaries_minor = (booking) => {
  const { pricingBreakdown = [] } = booking || {};
  return pricingBreakdown.reduce((acc, row) => {
    if (!row || row.category !== 'ancillary') return acc;
    if (!SHOPPED_ANCILLARY_TYPES.has(row.type)) return acc;
    if (is_bundle_service(row.type, row.id)) return acc;
    const value = as_number(row.price);
    return Number.isNaN(value) ? acc : acc + value;
  }, 0);
};

/**
 * Kyte markup tier base in minor units: grandTotal − ancillaries − cardFees.
 * Mirrors the BE markupToApplyKyte so shop (normalize) and retrieve
 * (build_retrieve_price) select the same band. `amount_of` reads a row's minor
 * amount — retrieve rows use `price`, shop rows use `amount`.
 *
 * @param {number} grand_total_minor
 * @param {Array<object>} rows pricingBreakdown rows (retrieve flat / shop flattened)
 * @param {(row: object) => number|string} amount_of
 * @param {number} [card_fee_minor]
 * @returns {number} minor units
 */
export const get_kyte_markup_base_minor = (
  grand_total_minor,
  rows,
  amount_of,
  card_fee_minor = 0
) => {
  const ancillaries_minor = as_array(rows).reduce(
    (sum, row) => (is_ancillary_row(row) ? sum + as_number(amount_of(row)) : sum),
    0
  );
  return grand_total_minor - ancillaries_minor - as_number(card_fee_minor);
};

/**
 * Build the offer-level Price from a Kyte RetrieveBooking response.
 *
 * Contract:
 *   total      = totalCost − shopped (keeps fare-bundle premiums in
 *                fare price; future fee/tax rows flow into total too)
 *   grandTotal = booking.totalCost (already minor units, EUR)
 *   base       = grandTotal − all ancillaries − cardFees (= fare + taxes).
 *                This is the markup tier-selection base (get_markup's
 *                markup_base), mirroring the backend markupToApplyKyte
 *                exactly. Deriving it from the grand total rather than the
 *                `baseFare` row's `.price` keeps band-selection in lockstep
 *                with the BE recheck guard regardless of how Kyte itemizes
 *                taxes (the row's `.price` includes them today, but the
 *                grand-total form is robust if that ever changes).
 *
 * `taxes` / `fees` / `additionalServices` are left out so the caller's
 * spread preserves whatever the existing offer carried — Kyte doesn't
 * re-emit them on retrieve.
 *
 * @param {object} booking
 * @returns {object|null} canonical Price, or null if totalCost is missing
 */
export const build_retrieve_price = (booking) => {
  const { totalCost, currency = {} } = booking || {};
  if (totalCost === null || totalCost === undefined) return null;
  const grand_total_minor = as_number(totalCost);
  if (Number.isNaN(grand_total_minor)) return null;
  const decimals = currency.decimals !== undefined ? currency.decimals : 2;
  const shopped_minor = sum_shopped_ancillaries_minor(booking);
  const total_minor = grand_total_minor - shopped_minor;
  // cardFees is usually absent at FE pricing time (the BE may inject it later).
  const { fee: card_fee_minor = 0 } = booking.cardFees || {};
  const base_minor = get_kyte_markup_base_minor(
    grand_total_minor,
    booking.pricingBreakdown,
    (row) => row.price,
    card_fee_minor
  );
  const currencyCode = currency.code || 'EUR';
  return {
    total: minor_to_decimal(total_minor, decimals),
    grandTotal: minor_to_decimal(grand_total_minor, decimals),
    base: minor_to_decimal(base_minor, decimals),
    _markup_base: minor_to_decimal(base_minor, decimals),
    currency: currencyCode,
    currencySymbol: currency_symbol(currencyCode),
  };
};

/**
 * Index pricingBreakdown rows two ways so enrich_with_breakdown_price
 * can match ancillary lines against Kyte's coarser pricing model:
 *   - byId:      `${pax}:${type}:${bare_id}` → minor units
 *   - byPaxType: `${pax}:${type}` → { sum, codes }
 *
 * Kyte aggregates ancillary prices per (passenger, type, code) — never
 * per booked line — and uses synthetic ids for seats (`SEAT-N`). The
 * (pax, type) summary covers both: bag-code drift (ancillary `BBG` vs
 * breakdown `BBG1`) via codes[], and seat aggregation via sum.
 *
 * @param {object} booking
 * @returns {{byId: Object<string, number>, byPaxType: Object<string, {sum: number, codes: string[]}>}}
 */
const build_ancillary_price_index = (booking) => {
  const empty = { byId: {}, byPaxType: {} };
  if (!booking) return empty;
  const { pricingBreakdown = [] } = booking;
  return pricingBreakdown.reduce((index, row) => {
    const {
      category = '',
      type = '',
      passengerId = '',
      id = '',
      price = 0,
    } = row || {};
    if (category !== 'ancillary' || !passengerId || !type || !id) {
      return index;
    }
    if (is_bundle_service(type, id)) return index;
    const amount = as_number(price);
    if (Number.isNaN(amount)) return index;
    const bare_id = strip_pax_suffix(id);
    index.byId[`${passengerId}:${type}:${bare_id}`] = amount;
    const pax_type_key = `${passengerId}:${type}`;
    const { sum = 0, codes = [] } = index.byPaxType[pax_type_key] || {};
    index.byPaxType[pax_type_key] = {
      sum: sum + amount,
      codes: codes.concat([bare_id]),
    };
    return index;
  }, empty);
};

/**
 * Resolve an ancillary line to the bare_id of the pricingBreakdown row
 * that covers it. Returns "" when nothing matches — that's a genuine
 * fare-bundle freebie (or a bundle-service row already filtered out).
 *
 * @param {{type: string, id: string}} item
 * @param {{codes: string[]}} pax_type per-(pax, type) summary from build_ancillary_price_index
 * @returns {string} matched bare_id, or ""
 */
const resolve_breakdown_code = (item, pax_type) => {
  if (!pax_type) return '';
  const { type = '', id = '' } = item || {};
  const codes = pax_type.codes || [];
  if (id && codes.indexOf(id) !== -1) return id;
  if (id) {
    // Committed ids are UPPERCASE + count-less ("CHECKED-IN-BAG-32");
    // breakdown codes are lowercase + counted ("checked-in-bag-32|count=2").
    // Prefer a count-exact match first (strip_meta keeps |count=N): a
    // count=2 id must never bind to a count=1 row when both are present.
    const item_meta = strip_meta(id);
    if (item_meta) {
      const meta_hit = codes.find(
        (code) => code && strip_meta(code) === item_meta
      );
      if (meta_hit) return meta_hit;
    }
    // Fall back to the count-agnostic base so a count-less committed id still
    // finds its counted breakdown row (else it prices 0 and mis-flags
    // `included`, hiding the bag from the post-book breakdown).
    const item_base = base_code(id);
    if (item_base) {
      const base_hit = codes.find(
        (code) => code && base_code(code) === item_base
      );
      if (base_hit) return base_hit;
    }
  }
  if (id) {
    // Bidirectional prefix match — require both sides have length >= 3
    // so two-letter codes (e.g. "BB") don't spuriously match a longer
    // code (e.g. "BBG") sharing the same opener.
    const MIN_PREFIX_LEN = 3;
    const prefix_hit = codes.find((code) => {
      if (!code) return false;
      if (
        code.length >= MIN_PREFIX_LEN &&
        id.length >= MIN_PREFIX_LEN &&
        code.indexOf(id) === 0
      ) {
        return true;
      }
      if (
        id.length >= MIN_PREFIX_LEN &&
        code.length >= MIN_PREFIX_LEN &&
        id.indexOf(code) === 0
      ) {
        return true;
      }
      return false;
    });
    if (prefix_hit) return prefix_hit;
  }
  if (type === 'seat' && codes.length === 1 && pax_type.sum > 0) {
    return codes[0];
  }
  return '';
};

/**
 * Count committed ancillary lines per pricingBreakdown row they map to.
 * Used to split the row's per-(pax, type, code) total across the lines
 * so per-line prices sum back to the aggregate (Kyte's pricingBreakdown
 * never breaks ancillary cost down per segment).
 *
 * @param {object} booking
 * @param {{byPaxType: Object<string, {sum: number, codes: string[]}>}} price_index
 * @returns {Object<string, number>} key `${pax}:${type}:${bare_id}` → count
 */
const tally_breakdown_line = (counts, byPaxType, pax, item) => {
  const { type = '', id = '' } = item || {};
  if (!pax || !type) return;
  if (is_bundle_service(type, id)) return;
  const pax_type = byPaxType[`${pax}:${type}`];
  const matched = resolve_breakdown_code(item, pax_type);
  if (!matched) return;
  const key = `${pax}:${type}:${matched}`;
  counts[key] = (counts[key] || 0) + 1;
};

const count_lines_per_breakdown_row = (booking, price_index) => {
  const counts = {};
  const { byPaxType = {} } = price_index || {};
  const { ancillaries = [], passengers = [] } = booking || {};
  const top = Array.isArray(ancillaries) ? ancillaries : [];
  top.forEach((entry) => {
    const { id: pax = '', ancillaries: items = [] } = entry || {};
    if (!Array.isArray(items)) return;
    items.forEach((item) => tally_breakdown_line(counts, byPaxType, pax, item));
  });
  const pax_list = Array.isArray(passengers) ? passengers : [];
  pax_list.forEach((pax_entry) => {
    const { id: pax = '', ancillaries: items = [] } = pax_entry || {};
    if (!Array.isArray(items)) return;
    items.forEach((item) => tally_breakdown_line(counts, byPaxType, pax, item));
  });
  return counts;
};

/**
 * Attach a pricingBreakdown-derived price to an ancillary entry. Kyte's
 * retrieve doesn't price ancillaries per booked line — pricingBreakdown
 * aggregates by (pax, type, code) and uses synthetic ids for seats —
 * so a tiered lookup is needed:
 *   1. exact `(pax, type, id)` match (most bag codes)
 *   2. prefix match within `(pax, type)` (ancillary "BBG" vs breakdown "BBG1")
 *   3. seat aggregate split across seat lines for the pax (synthetic SEAT-N)
 *   4. no breakdown row anywhere for (pax, type) → genuine fare-bundle
 *      freebie (e.g. Plus's bundled 20kg bag), included: true
 *
 * Per-line prices via (2) and (3) are approximate by design — the
 * source-of-truth totals live on price.additionalServices[].price via
 * build_additional_services.
 *
 * @param {object} item ancillary entry from booking.ancillaries[]
 * @param {string} traveler_id
 * @param {{byId: Object<string, number>, byPaxType: Object<string, {sum: number, codes: string[]}>}} price_index
 * @param {Object<string, number>} share_counts `${pax}:${type}:${bare_id}` → ancillary lines covered by that breakdown row
 * @param {number} decimals currency decimals
 * @param {string} currencyCode
 * @returns {object} enriched ancillary
 */
const enrich_with_breakdown_price = (
  item,
  traveler_id,
  price_index,
  share_counts,
  decimals,
  currencyCode
) => {
  const safe = item || {};
  const { byId = {}, byPaxType = {} } = price_index || {};
  const pax = traveler_id || '';
  const type = safe.type || '';
  const enriched = { travelerId: pax, ...safe };

  const pax_type = byPaxType[`${pax}:${type}`];
  const matched_code = resolve_breakdown_code(safe, pax_type);
  if (matched_code) {
    const total = byId[`${pax}:${type}:${matched_code}`];
    const share =
      (share_counts && share_counts[`${pax}:${type}:${matched_code}`]) || 1;
    const per_line = Math.round(total / share);
    enriched.price = make_price(
      minor_to_decimal(per_line, decimals),
      currencyCode
    );
    enriched.included = false;
    return enriched;
  }

  enriched.price = make_price(minor_to_decimal(0, decimals), currencyCode);
  enriched.included = true;
  return enriched;
};

const committed_ancillary_key = (traveler_id, id, flight_segments) => {
  const segments = Array.isArray(flight_segments)
    ? [...flight_segments].sort()
    : [];
  return `${traveler_id}|${id}|${segments.join(',')}`;
};

/**
 * Kyte (e.g. Volotea) can emit the same committed ancillary twice: once
 * under its specific type (`bag`, `seat`, …) and again as a generic
 * `service` carrying the raw carrier SSR code (CABIN-10 → "PBRD"). Only the
 * specific row is priced in pricingBreakdown, so the `service` twin is a
 * price-0 duplicate that also surfaces under SERVICES. Keep the specific
 * record and drop the `service` copy whenever a same (traveler, id,
 * segment) non-service row exists.
 *
 * @param {Array<object>} committed flattened committed ancillaries
 * @returns {Array<object>} committed without the redundant `service` twins
 */
const drop_duplicated_service_ancillaries = (committed) => {
  const specific_keys = new Set();
  committed.forEach((entry) => {
    const { type = '', id = '', travelerId = '', flightSegments = [] } =
      entry || {};
    if (type === 'service' || !id) return;
    specific_keys.add(committed_ancillary_key(travelerId, id, flightSegments));
  });
  if (!specific_keys.size) return committed;
  return committed.filter((entry) => {
    const { type = '', id = '', travelerId = '', flightSegments = [] } =
      entry || {};
    if (type !== 'service') return true;
    return !specific_keys.has(
      committed_ancillary_key(travelerId, id, flightSegments)
    );
  });
};

/**
 * Realign committed `flightSegments` onto the booking's own itinerary
 * segment ids. Kyte (e.g. Volotea) stamps ancillaries with the
 * carrier-prefixed id ("V71533-VRN-PMO") while the itinerary carries the
 * bare id ("1533-VRN-PMO"); left unreconciled, the per-(pax, segment) stamp
 * and the booked displays never match. No-op when the booking ships no
 * itinerary segments (every current test fixture) or when an id can't be
 * reconciled (kept as-is).
 *
 * @param {Array<object>} committed flattened committed ancillaries
 * @param {object} booking RetrieveBooking / ConfirmBooking
 * @returns {Array<object>} committed with flightSegments realigned to bare ids
 */
const normalize_committed_segments = (committed, booking) => {
  const { itinerary = {} } = booking || {};
  const { flightSolutions = {} } = itinerary;
  const bare_ids = {};
  Object.keys(flightSolutions).forEach((key) => {
    const solution = flightSolutions[key] || {};
    const segments = Array.isArray(solution.segments) ? solution.segments : [];
    segments.forEach((segment) => {
      if (segment && segment.id) {
        bare_ids[segment.id] = segment.id;
      }
    });
  });
  if (!Object.keys(bare_ids).length) {
    return committed;
  }
  const { code: carrier_iata = '' } = (booking || {}).ticketingCarrier || {};
  return committed.map((entry) => ({
    ...entry,
    flightSegments: realign_segment_ids(
      entry.flightSegments,
      bare_ids,
      carrier_iata,
      true
    ),
  }));
};

/**
 * Flatten committed ancillaries to a single shape regardless of whether
 * Kyte ships them under `booking.ancillaries[]` (current) or
 * `booking.passengers[].ancillaries[]` (legacy) — never both. Prices are
 * pulled from booking.pricingBreakdown via enrich_with_breakdown_price.
 * Bundle service rows are dropped here so they don't surface as shopped.
 *
 * @param {object} booking
 * @param {number} decimals currency decimals
 * @param {string} currencyCode
 * @returns {Array<object>} `{travelerId, type, id, quantity, flightSegments, price, included}[]`
 */
export const extract_committed_ancillaries = (
  booking,
  decimals,
  currencyCode
) => {
  if (!booking) return [];
  const price_index = build_ancillary_price_index(booking);
  const share_counts = count_lines_per_breakdown_row(booking, price_index);
  const out = [];
  const top = Array.isArray(booking.ancillaries) ? booking.ancillaries : [];
  const top_has_entries = top.some((entry) => {
    const safe = entry || {};
    return (
      (Array.isArray(safe.ancillaries) && safe.ancillaries.length > 0) ||
      (safe.id && safe.type)
    );
  });
  if (top_has_entries) {
    top.forEach((entry) => {
      const safe = entry || {};
      const inner = Array.isArray(safe.ancillaries) ? safe.ancillaries : null;
      if (inner) {
        inner.forEach((item) => {
          if (!item) return;
          if (is_bundle_service(item.type, item.id)) return;
          out.push(
            enrich_with_breakdown_price(
              item,
              safe.id || '',
              price_index,
              share_counts,
              decimals,
              currencyCode
            )
          );
        });
        return;
      }
      if (safe.id && safe.type) {
        if (is_bundle_service(safe.type, safe.id)) return;
        out.push(
          enrich_with_breakdown_price(
            safe,
            safe.travelerId || '',
            price_index,
            share_counts,
            decimals,
            currencyCode
          )
        );
      }
    });
    return drop_duplicated_service_ancillaries(
      normalize_committed_segments(out, booking)
    );
  }
  const passengers = Array.isArray(booking.passengers)
    ? booking.passengers
    : [];
  passengers.forEach((pax) => {
    const safe = pax || {};
    const items = Array.isArray(safe.ancillaries) ? safe.ancillaries : [];
    items.forEach((item) => {
      if (!item) return;
      if (is_bundle_service(item.type, item.id)) return;
      out.push(
        enrich_with_breakdown_price(
          item,
          safe.id || '',
          price_index,
          share_counts,
          decimals,
          currencyCode
        )
      );
    });
  });
  return drop_duplicated_service_ancillaries(
    normalize_committed_segments(out, booking)
  );
};
